import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import ViewScenarioTitlePage from './ViewScenarioTitlePage';
import ViewSituation from './ViewSituation';
import { useParams, useMatch, useNavigate, useLocation } from 'react-router-dom';
import { useScenario } from '../../../context/ScenarioContext';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import { getUser } from '../../../services/UserServices';
import { getAssignmentSkipStatus } from '../../../services/AssignmentServices';
import { getCanvas, getPublishedMap } from '../../../services/CanvasServices';
import { createDraftSituationSource } from '../../../utils/draftPlayAdapter';
import ViewTableOfContents from './ViewTableOfContents';
import styles from './styles/ViewScenario.module.css';
import AuthContext from '../../../context/AuthContext';
import ScenarioPassword from './ScenarioPassword';
import { FaListOl, FaRedo, FaTimes } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import Loading from '../../loading/Loading.js';
import socket, { addJoinEvent, removeJoinEvent } from './../../../socket';
import useAssignmentProgress from '../../../hooks/useAssignmentProgress';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../tutorials';
import PreviewKeyTray from './PreviewKeyTray';
import PreviewKeysPrompt from './PreviewKeysPrompt';
import usePlayerKeys from '../../../hooks/usePlayerKeys';

const ViewScenario = () => {
    const location = useLocation();
    const { userId: authUserId, user } = useContext(AuthContext);
    const { assignmentId, scenarioId } = useParams();
    //console.log(user);
    //console.log(scenarioId);
    const [scenario, setScenario] = useState(null);
    const [canvas, setCanvas] = useState(null);
    const { currentSituations, setCurrentSituationId: contextSetCurrentSituationId, resumeLog, currentLogInfo } = useScenario();
    const [hasAccess, setHasAccess] = useState(location.state?.hasAccess ?? false);
    const [fromFeedback, setFromFeedback] = useState(location.state?.fromFeedback ?? false);
    const [passwordError, setPasswordError] = useState(false);
    const [isToCVisible, setIsToCVisible] = useState(false);
    const preview = useMatch("/create/:scenarioId/preview");
    const navigate = useNavigate();

    const [fetchedUser, setFetchedUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [isOverallLoading, setIsOverallLoading] = useState(true);
    const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
    const [isScenarioDataLoaded, setIsScenarioDataLoaded] = useState(false);
    const [isLogResolved, setIsLogResolved] = useState(false);

    const [disableRestart, setDisableRestart] = useState(false);
    const [disableToC, setDisableToC] = useState(false);
    const [booleansLoaded, setBooleansLoaded] = useState(false);

    const [visitedSituationPreview, setVisitedSituationsPreview] = useState([]);

    // Previewing keeps keys on this device only, never on the author's account;
    // players keep them in the session (logged out) or on their account.
    const {
        userKeys,
        keysLoaded,
        applyKeyChanges,
        clearScenarioKeys,
        lastChange: lastKeyChange,
    } = usePlayerKeys({ scenarioId, userId: authUserId, preview: !!preview });
    // 'restart' | 'exit' while the preview asks whether to keep held keys.
    const [keysPromptAction, setKeysPromptAction] = useState(null);

    useEffect(() => {
        if (currentLogInfo) {
            console.log("Updated currentLogInfo", currentLogInfo);
        }
    }, [currentLogInfo]);

    const currentSituationId = currentSituations[scenarioId];


    useEffect(() => {
        if (location.state?.currentSituationId && scenarioId) {
            contextSetCurrentSituationId(scenarioId, location.state.currentSituationId);
        }
    }, [location.state?.currentSituationId, scenarioId, contextSetCurrentSituationId]);


    useEffect(() => {
        const fetchBooleans = async () => {
            if (assignmentId) {
                try {
                    const data = await getAssignmentSkipStatus(assignmentId);
                    if (data.restart) setDisableRestart(data.restart);
                    if (data.ToC) setDisableToC(data.ToC);
                } catch (error) {
                    console.error("Failed to fetch skip status booleans:", error);
                }
            }
            setBooleansLoaded(true);
        };
        fetchBooleans();
    }, [assignmentId]);

    useEffect(() => {
        setIsUserDataLoaded(false);
        setIsOverallLoading(true);
        setUserLoading(true);

        const fetchUserData = async () => {
            if (!authUserId) {
                setFetchedUser(null);
                setIsUserDataLoaded(true);
                setUserLoading(false);
                return;
            }

            try {
                const userData = await getUser(authUserId, { populateAssignments: true });
                setFetchedUser(userData);
            } catch (error) {
                console.error('Error fetching user data:', error);
                setFetchedUser(null);
            } finally {
                setIsUserDataLoaded(true);
                setUserLoading(false);
            }
        };

        fetchUserData();
    }, [authUserId, scenarioId]);

    useEffect(() => {
        setIsScenarioDataLoaded(false);
        setIsOverallLoading(true);

        const fetchScenarioData = async () => {
            if (!scenarioId) return;
            try {
                // Preview plays the DRAFT, so it loads the full canvas that only an
                // owner or collaborator can see — groups and table of contents
                // included, since those live in the draft too. Every other path
                // takes the published skeleton, which carries positions and titles
                // and nothing an author has not published yet.
                const [scenarioData, canvasData] = await Promise.all([
                    ScenarioAPI.getScenario(scenarioId),
                    preview ? getCanvas(scenarioId) : getPublishedMap(scenarioId).catch(() => null),
                ]);

                setScenario(scenarioData);
                setCanvas(canvasData);
                setPasswordError(false);
            } catch (error) {
                console.error(`Error fetching scenario data: ${error}`);
                setScenario(null);
                setCanvas(null);
            } finally {
                setIsScenarioDataLoaded(true);
            }
        };

        fetchScenarioData();
    }, [scenarioId, preview]);

    /**
     * Where a situation comes from.
     *
     * Playing a published scenario reads the published Situation/Choice
     * documents, exactly as before. Previewing reshapes the draft the editor just
     * saved into the same objects, so an author sees the edits they made rather
     * than the last published version — and the published play path is untouched
     * either way.
     */
    const situationSource = useMemo(() => {
        if (!preview || !canvas) return undefined;
        return createDraftSituationSource({
            nodes: canvas.nodes || [],
            edges: canvas.edges || [],
            groups: canvas.groups || [],
        });
    }, [preview, canvas]);

    /**
     * Which table of contents to show.
     *
     * Preview shows the DRAFT's, so an author previewing sees the sections they
     * just added rather than the last published version — the same rule the graph
     * itself follows. Items whose situation is no longer in the draft are dropped
     * here, mirroring what publish prunes, so a deleted node cannot leave a dead
     * link in the preview.
     */
    const activeTableOfContents = useMemo(() => {
        if (!preview) return scenario?.tableOfContents ?? null;

        const draft = canvas?.tableOfContents;
        if (!draft?.contents?.length) return null;

        const liveIds = new Set((canvas?.nodes || []).map((node) => String(node.id)));
        const isLive = (item) => item?.situation && liveIds.has(String(item.situation));

        return {
            ...draft,
            contents: draft.contents.map((content) => ({
                ...content,
                sections: (content.sections || []).map((section) => ({
                    ...section,
                    items: (section.items || []).filter(isLive),
                })),
                items: (content.items || []).filter(isLive),
            })),
        };
    }, [preview, scenario?.tableOfContents, canvas]);

    // Resume the server-side log on entry. For logged, non-preview scenarios this
    // rehydrates the log id + last position from the canonical in-progress log
    // (works across devices, and keeps filling the same log). Other scenarios have
    // no server log, so resolution is immediate.
    useEffect(() => {
        setIsLogResolved(false);

        if (!isScenarioDataLoaded) return;

        if (preview || !scenario?.isLogged || !scenario?._id) {
            setIsLogResolved(true);
            return;
        }

        // An explicit deep-link target (e.g. jump-from-feedback) should win over the
        // saved log position, but we still want the log id attached.
        const applyPosition = !location.state?.currentSituationId;

        let cancelled = false;
        (async () => {
            await resumeLog(scenario._id, { applyPosition });
            if (!cancelled) setIsLogResolved(true);
        })();

        return () => { cancelled = true; };
    }, [isScenarioDataLoaded, scenario?._id, scenario?.isLogged, preview, resumeLog, location.state?.currentSituationId]);


    useEffect(() => {
        if (isUserDataLoaded && isScenarioDataLoaded && isLogResolved && keysLoaded) {
            let finalAccess = false;
            if (preview) {
                finalAccess = true;
            } else if (scenario && !scenario.isPrivate) {
                finalAccess = true;
            } else if (authUserId && fetchedUser?.activeAssignments?.some(a => a?.scenario?._id === scenarioId)) {
                finalAccess = true;
            } else if (location.state?.hasAccess === true) {

                finalAccess = true;
            }

            if (finalAccess) {
                setHasAccess(true);
            } else if (!location.state?.hasAccess) {
                setHasAccess(false);
            }

            setIsOverallLoading(false);
        } else {
            setIsOverallLoading(true);
        }
    }, [
        isUserDataLoaded, isScenarioDataLoaded, isLogResolved, keysLoaded, preview, scenario,
        authUserId, fetchedUser, scenarioId, location.state?.hasAccess, setHasAccess
    ]);

    const [universalLock, setUniversalLock] = useState(false);
    const [userLock, setUserLock] = useState(false);

    const handleLockUpdate = useCallback((data) => {
        setUniversalLock(data.universalLock);
    }, [setUniversalLock]);

    const handleUpdateUserLock = useCallback((data) => {
        setUserLock(data.lockedBool);
    }, [setUserLock]);

    // The join ack carries the assignment's current lock state, so a student
    // who joins (or reconnects after a server cold start) while locked is
    // locked immediately — no teacher-side re-broadcast needed.
    const handleJoinAck = useCallback((err, response) => {
        if (err || response?.status !== 'OK') return;
        setUniversalLock(Boolean(response.locks?.universal));
        setUserLock(Boolean(response.youAreLocked));
    }, []);

    const { reportProgress } = useAssignmentProgress({ assignmentId, userId: authUserId, userInfo: user });

    // Latest position, readable by the join effect below without re-running it
    // on every navigation. (Declared before that effect so it syncs first.)
    const currentSituationIdRef = useRef(null);
    useEffect(() => {
        currentSituationIdRef.current = currentSituationId ?? null;
    }, [currentSituationId]);

    useEffect(() => {
        if (assignmentId && authUserId && user) {
            const joinParams = {
                assignmentId,
                userId: authUserId,
                userName: user.username,
                userEmail: user.email,
                situationId: currentSituationIdRef.current,
            };

            addJoinEvent('joinAssignmentRoom', joinParams, handleJoinAck);

            socket.on('universalLockUpdate', handleLockUpdate);
            socket.on('updateUserLock', handleUpdateUserLock);

            return () => {
                removeJoinEvent('joinAssignmentRoom');

                socket.off('universalLockUpdate', handleLockUpdate);
                socket.off('updateUserLock', handleUpdateUserLock);
            };
        }
    }, [
        assignmentId,
        authUserId,
        user,
        handleLockUpdate,
        handleUpdateUserLock,
        handleJoinAck
    ]);

    // Single reporting site for the live teacher view: every way the student's
    // position can change (choice click, free response, skip, ToC jump, start,
    // resumed log) flows through currentSituationId.
    useEffect(() => {
        if (assignmentId && currentSituationId && !preview) {
            reportProgress(currentSituationId);
        }
    }, [assignmentId, currentSituationId, preview, reportProgress]);

    useEffect(() => {
        return () => {
            socket.emit('leaveAssignmentRoom', { assignmentId, userId: authUserId });
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentSituationId]);

    // The draft's keys the author is holding, by name, for the preview prompt.
    const heldPreviewKeyNames = useMemo(() => {
        if (!preview) return [];
        const held = new Set(userKeys.map(String));
        return (canvas?.keys || [])
            .filter((key) => held.has(String(key.id)))
            .map((key) => key.name);
    }, [preview, userKeys, canvas?.keys]);

    const restart = useCallback(async ({ clearKeys }) => {
        if (scenario?._id) {
            contextSetCurrentSituationId(scenario._id, null);
            if (clearKeys) await clearScenarioKeys();
        }
    }, [scenario, contextSetCurrentSituationId, clearScenarioKeys]);

    const exitPreview = useCallback(async ({ clearKeys }) => {
        if (scenario?._id) {
            // Preview keys live on this device, so clearing is immediate and
            // finishes before the page is left.
            if (clearKeys) await clearScenarioKeys();
            contextSetCurrentSituationId(scenario._id, null);
            // Hand back what the editor had open so it can pick up where it was.
            navigate(`/create/${scenarioId}/canvas`, {
                state: { restoreSelection: location.state?.returnTo ?? null },
            });
        }
    }, [scenario, scenarioId, navigate, contextSetCurrentSituationId, clearScenarioKeys, location.state?.returnTo]);

    // A player's restart always clears their keys. A preview holding keys asks
    // first, so an author can keep them for testing.
    const handleRestart = useCallback(() => {
        if (preview && heldPreviewKeyNames.length > 0) {
            setKeysPromptAction('restart');
            return;
        }
        restart({ clearKeys: true });
    }, [preview, heldPreviewKeyNames.length, restart]);

    const handleExitPreview = useCallback(() => {
        if (heldPreviewKeyNames.length > 0) {
            setKeysPromptAction('exit');
            return;
        }
        exitPreview({ clearKeys: true });
    }, [heldPreviewKeyNames.length, exitPreview]);

    const resolveKeysPrompt = useCallback((clearKeys) => {
        const action = keysPromptAction;
        setKeysPromptAction(null);
        if (action === 'restart') restart({ clearKeys });
        else if (action === 'exit') exitPreview({ clearKeys });
    }, [keysPromptAction, restart, exitPreview]);

    const cancelKeysPrompt = useCallback(() => setKeysPromptAction(null), []);

    const handleReturnToFeedback = useCallback(() => {
        if (fetchedUser) {
            navigate(`/user/${fetchedUser.username}`, { state: { activeTab: 'Feedback' } });
        }
        setFromFeedback(false);
    }, [fetchedUser, navigate]);

    const toggleToC = useCallback(() => {
        setIsToCVisible(prev => !prev);
    }, []);

    const setCurrentSituationIdWrapper = useCallback((situationId) => {
        if (scenario?._id) {
            contextSetCurrentSituationId(scenario._id, situationId);
        }
    }, [scenario?._id, contextSetCurrentSituationId]);

    const [fromToC, setFromToC] = useState(false);

    const handleToC = useCallback((val) => {
        setFromToC(val);
    },[fromToC]);

    if (isOverallLoading) {
        return <Loading />;
    }

    if (!hasAccess && scenario?.isPrivate && !preview) {
        return (
            <ScenarioPassword
                scenarioId={scenario._id}
                setHasAccess={setHasAccess}
                passwordError={passwordError}
                setPasswordError={setPasswordError}
            />
        );
    }

    if (!hasAccess || !scenario) {
        return <div>Access Denied or Scenario not found.</div>;
    }

    return (
        <>
            {fromFeedback && !preview && (
                <button className={styles.previewButton} onClick={handleReturnToFeedback}>Back</button>
            )}

            {!currentSituationId ? (
                <ViewScenarioTitlePage
                    scenario={scenario}
                    canvas={canvas}
                    setCurrentSituationId={setCurrentSituationIdWrapper}
                    preview={preview}
                    clearScenarioKeys={clearScenarioKeys}
                    assignmentId = {assignmentId}
                />
            ) : (
                <div className={styles.container}>
                    {(booleansLoaded && !disableToC && activeTableOfContents && !activeTableOfContents.disabled) && (
                        <>
                            <div className={styles.header}>
                                <FaListOl
                                    className={styles.tocButton}
                                    onClick={toggleToC}
                                    data-tooltip-id="viewscenario-tooltip"
                                    data-tooltip-content="Table of Contents"
                                />
                                <div className={styles.tocTitle}>Table of Contents</div>
                            </div>
                            {(booleansLoaded && (isToCVisible && !disableToC && !universalLock && !userLock)) && (
                                <ViewTableOfContents
                                    tableOfContents={activeTableOfContents}
                                    currentSituationId={currentSituationId}
                                    setCurrentSituationId={setCurrentSituationIdWrapper}
                                    toggleToC={toggleToC}
                                    handleFromToC = {handleToC}
                                    currentLogInfo={currentLogInfo}
                                    preview={preview}
                                    visitedSituationPreview={visitedSituationPreview}
                                />
                            )}
                        </>
                    )}
                    <ViewSituation
                        userId={authUserId}
                        scenario={scenario}
                        canvas={canvas}
                        currentSituationId={currentSituationId}
                        setCurrentSituationId={setCurrentSituationIdWrapper}
                        preview={preview}
                        userLoading={userLoading || !keysLoaded}
                        userKeys={userKeys}
                        applyKeyChanges={applyKeyChanges}
                        clearScenarioKeys={clearScenarioKeys}
                        assignmentId = {assignmentId}
                        fromToC = {fromToC}
                        handleFromToC = {handleToC}
                        universalLock={universalLock}
                        userLock={userLock}
                        situationSource={situationSource}
                        visitedSituationPreview={visitedSituationPreview}
                        setVisitedSituationsPreview={setVisitedSituationsPreview}
                    />
                    {(preview || (booleansLoaded && !disableRestart)) && (
                        <div className={styles.actions}>
                            {preview && (
                                <button
                                    type="button"
                                    aria-label="Exit Preview"
                                    className={styles.iconButton}
                                    onClick={handleExitPreview}
                                    data-tooltip-id="viewscenario-tooltip"
                                    data-tooltip-content="Exit Preview"
                                    {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_EXIT)}
                                >
                                    <FaTimes />
                                </button>
                            )}
                            {(booleansLoaded && !disableRestart) && (
                                <button
                                    type="button"
                                    aria-label="Restart Scenario"
                                    className={styles.iconButton}
                                    onClick={handleRestart}
                                    data-tooltip-id="viewscenario-tooltip"
                                    data-tooltip-content="Restart Scenario"
                                    {...tutorialAttr(TUTORIAL_TARGETS.PREVIEW_RESTART)}
                                >
                                    <FaRedo />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
            {/* Outside the branch so keys kept from a restart show on the title page too. */}
            {preview && (
                <PreviewKeyTray
                    keys={canvas?.keys || []}
                    userKeys={userKeys}
                    lastChange={lastKeyChange}
                />
            )}
            {preview && keysPromptAction && (
                <PreviewKeysPrompt
                    action={keysPromptAction}
                    keyNames={heldPreviewKeyNames}
                    onClear={() => resolveKeysPrompt(true)}
                    onKeep={() => resolveKeysPrompt(false)}
                    onCancel={cancelKeysPrompt}
                />
            )}
            <Tooltip id="viewscenario-tooltip" place="bottom" style={{ zIndex: 9999 }} />
        </>
    );
};

export default ViewScenario;